// BCM-898 · PA3 — Review consumption (slides 8, 9, 10). [Uday]
// Status 55 AND reviewConsumptionReqd AND approveFGConsumption. Renders the Review
// Consumption table INLINE (mockup slide 8) with the four new columns (Storage
// Location ID, Date of Manufacturing, Expiry Date, Deplete Instruction), the two
// allocated-batch error lines, and Change Allocation / Upload Allocation / Confirm
// Consumption. Sync Inventory sits top-right and re-runs the validation. Change
// Allocation reuses the BATCH DASHBOARD dialog
// (sasl/deliveryScheduleBatchTable/EditPlannedAllocatedQuantityDialog) — the same
// popup Batch Execution's ReviewPopUp opens — now that BCM-363 (Date of
// Manufacturing + Deplete Instruction) has been pulled into it. That dialog owns
// the material radio selector and the CMO | Plant ID | Material BOM Qty. | UOM
// ribbon internally, so it matches the GR mockup as-is. The ONLY GR-specific
// behaviour is passed as a flag (`isFromGoodsReceipt`) so the shared dialog stays
// byte-identical for Batch Execution, which passes nothing: it restricts the
// allocation table to Special-Stocks-with-Vendor rows (consumption may only draw
// from SSwV), matching what the PO-dashboard copy did via disableOtherStorageLocations.
// Upload Allocation reuses the PO dashboard's "Allocate POs" bulk-upload modal
// (AllocatePOsModal) instead of a bespoke uploader. Confirm calls
// formConsumptionFromReview with the payload ReviewPopUp.jsx sends (unchanged
// processing, per spec) and then shows the confirmed state (slide 10).
//
// Field mapping — A5 fetchComponentAllocationForPOLineNewDirect (verified against
// njm-corda-pcmp/controllers/Direct/fetchComponentAllocationForPOLineNewDirect.js):
//   componentAllocationDetails.componentCode / componentName -> BMS Material Code / Description
//   componentAllocationDetails.componentQuantity             -> Material BoM Qty.  (NOT `bomQuantity`,
//                                                               which the API doc names but the
//                                                               handler never emits; boComponentQuantity
//                                                               is the CMO-side twin)
//   componentAllocationDetails.uom                           -> UOM
//   lotNumberAllocationDetails[].storageLocation             -> Storage Location ID (null/""/0 = SSwV)
//   lotNumberAllocationDetails[].lotNumber                   -> BMS Material Batch Number
//   lotNumberAllocationDetails[].manufacturingStartDate      -> Date of Manufacturing
//   lotNumberAllocationDetails[].expiryDate                  -> Expiry Date
//   lotNumberAllocationDetails[].plannedAllocatedQuantity    -> Planned Allocated Qty.
//   lotNumberAllocationDetails[].isDepletedBatch             -> Deplete Instruction
// NB lotNumberAllocationDetails is NEVER empty: with nothing allocated the handler
// still pushes one entry carrying only the PO-configuration fields (no lotNumber,
// no plannedAllocatedQuantity). Those placeholders must not be rendered as rows,
// nor validated as batches — see isAllocatedLot below.
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Box, Button, Dialog, Checkbox,
} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import CheckIcon from "@material-ui/icons/Check";
import SectionShell from "../components/SectionShell";
import SyncActions from "../components/common/SyncActions";
import EditPlannedAllocatedQuantityDialog from "../../sasl/deliveryScheduleBatchTable/EditPlannedAllocatedQuantityDialog";
import AllocatePOsModal from "../../PurchaseOrderDashbaordIndex/AllocatePOsModal";
import { fetchPOLineAllocation, roleUrl } from "../api/goodsReceiptApi";
import { useGRStyles } from "../goodsReceiptStyles";
import { useGRFeedback } from "../context/GRFeedbackContext";
import { isSpecialStockWithVendor, formatStorageLocation, isExpired, formatDate } from "../utils/formatters";

// A real allocation, as opposed to the config-only placeholder the handler emits
// when a component has nothing allocated against it.
const isAllocatedLot = (lot) =>
  !!(lot && lot.lotNumber && Number(lot.plannedAllocatedQuantity) > 0);

const allocatedLotsOf = (comp) =>
  ((comp && comp.lotNumberAllocationDetails) || []).filter(isAllocatedLot);

// Fixed three decimals, as on the PO dashboard's allocation table — applied to
// both Material BoM Qty. and Planned Allocated Qty. so the two line up on precision
// (BCM-898 UAT: BoM Qty. showed "158.070" against Planned Allocated Qty.'s "158.07").
const formatQty3 = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  const n = Number(raw);
  return Number.isFinite(n) ? n.toFixed(3) : "";
};

const formatBomQty = (details) => {
  const d = details || {};
  const raw = d.componentQuantity != null ? d.componentQuantity : d.boComponentQuantity;
  return formatQty3(raw);
};

// Blank (not "-NA-") when a date is absent — matches the batch dashboard's Review
// Consumption pop-up (ReviewPopUp.jsx), which renders "" rather than a placeholder
// for a missing Date of Manufacturing / Expiry Date.
const blankDate = (date) => {
  const formatted = formatDate(date);
  return formatted === "-NA-" ? "" : formatted;
};

// Date of Manufacturing for an allocated lot. `manufacturingStartDate` is what the
// A5 handler emits on the lot; the inventory record is only consulted as a fallback
// for the day the backend starts exposing the inventory manufacturing date there
// (the inventory table holds it as `inventoryManufacturingDate`, but neither the
// Direct handler nor the Corda flow currently maps it onto inventoryDetails).
const manufacturingDateOf = (comp, lot) => {
  if (lot.manufacturingStartDate) return lot.manufacturingStartDate;
  if (lot.dateOfManufacturing) return lot.dateOfManufacturing;
  const inventory = (comp && comp.inventoryDetails) || [];
  const sameSloc = inventory.find(
    (i) =>
      i &&
      i.inventoryBatchNumber === lot.lotNumber &&
      String(i.storageLocationID == null ? "" : i.storageLocationID) ===
        String(lot.storageLocation == null ? "" : lot.storageLocation)
  );
  const inv = sameSloc || inventory.find((i) => i && i.inventoryBatchNumber === lot.lotNumber);
  return (inv && (inv.inventoryManufacturingDate || inv.dateOfManufacturing)) || "";
};

const PA3ReviewConsumption = ({ row, detail, featuresAccess, viewOnly, onRefresh }) => {
  const classes = useGRStyles();
  const [alloc, setAlloc] = useState([]); // componentAllocationDataList
  const [allocRes, setAllocRes] = useState(null); // full data (for Change Allocation)
  const [loading, setLoading] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [uploadAllocationOpen, setUploadAllocationOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const feedback = useGRFeedback();
  const cancelRef = React.useRef(null);
  const confirmCancelRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving PA3 review consumption");
      if (confirmCancelRef.current) confirmCancelRef.current.cancel("leaving PA3 review consumption");
    };
  }, []);

  const load = React.useCallback(() => {
    if (!row || !row.schrockenSLID) return;
    setLoading(true);
    if (cancelRef.current) cancelRef.current.cancel("superseded PA3 fetch");
    const source = axios.CancelToken.source();
    cancelRef.current = source;
    fetchPOLineAllocation(row.schrockenSLID, source.token)
      .then((res) => {
        if (!mountedRef.current) return;
        if (res.data && res.data.status) {
          const list = (res.data.data && res.data.data.componentAllocationDataList) || [];
          setAlloc(list);
          setAllocRes(res.data.data);
          // Keep the Change Allocation selection pointing at the same material
          // across a reload (the object identity changes on every fetch).
          setSelectedComponent((prev) => {
            const code = prev && prev.componentAllocationDetails && prev.componentAllocationDetails.componentCode;
            const match =
              code &&
              list.find(
                (c) => c.componentAllocationDetails && c.componentAllocationDetails.componentCode === code
              );
            return match || list[0] || null;
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        setLoading(false);
      });
  }, [row]);
  useEffect(load, [load]);
  // The confirmed state belongs to the BATCH, not to the row object — after
  // confirming, the feedback layer refetches the table (a new row object, and
  // possibly still the old status while the Temporal workflow catches up), and the
  // button must stay in its confirmed state through that rather than flipping back
  // to an enabled "Confirm Consumption".
  const batchId = row && row.schrockenDeliveryScheduleBatchID;
  useEffect(() => {
    setConfirmed(false);
  }, [batchId]);

  // ── per-lot checks + error lines ──────────────────────────────────────────
  // One message per FAILED CHECK per allocated batch (spec: "One error message is
  // displayed for each failed check against each allocated batch").
  const errors = [];
  alloc.forEach((comp) => {
    allocatedLotsOf(comp).forEach((lot, li) => {
      const code = (comp.componentAllocationDetails || {}).componentCode;
      if (isExpired(lot.expiryDate)) {
        errors.push({
          id: `${code}-${lot.lotNumber}-${li}-expiry`,
          batch: lot.lotNumber,
          before: "Allocated batch ",
          after: ` has expired on ${formatDate(lot.expiryDate)}.`,
        });
      }
      if (!isSpecialStockWithVendor(lot.storageLocation)) {
        errors.push({
          id: `${code}-${lot.lotNumber}-${li}-sloc`,
          batch: lot.lotNumber,
          before: "Allocated batch ",
          after: ` is not at Special Stocks with Vendor storage location (currently ${lot.storageLocation}).`,
        });
      }
    });
  });

  const hasAnyLot = alloc.some((comp) => allocatedLotsOf(comp).length > 0);
  // ReviewPopUp.jsx's rule, kept verbatim: a component with no lot entries at all,
  // or any entry missing a lot number / planned quantity, blocks confirmation.
  const lotIncomplete = alloc.some((comp) => {
    const lots = (comp && comp.lotNumberAllocationDetails) || [];
    if (lots.length === 0) return true;
    return lots.some((l) => !l.lotNumber || !l.plannedAllocatedQuantity);
  });
  const confirmDisabled =
    viewOnly ||
    confirming ||
    errors.length > 0 ||
    !hasAnyLot ||
    lotIncomplete ||
    !(featuresAccess && featuresAccess.approveFGConsumption !== false);

  // ── Change Allocation popup inputs ────────────────────────────────────────
  // The batch-dashboard dialog reads the PO from `poData` FLAT (ReviewPopUp hands
  // it the whole batch row object as `batchRowData`). The Goods Receipt dashboard
  // assembles the same flat shape from the A1 row — which carries every one of
  // these fields (fetchBatchesDirect.js) — with the A5 response as the fallback
  // for the two it also returns. This feeds the popup title, the ribbon and,
  // more importantly, the SAVE payload (poNumber, poStatus, productCodeDiscription,
  // bomQty, uom) which the dialog and its child read as `poData?.<field>`.
  const poDataForDialog = React.useMemo(
    () => ({
      schrockenSLID: row && row.schrockenSLID,
      scheduleAgreementNumber:
        (row && row.scheduleAgreementNumber) || (allocRes && allocRes.poNumber),
      scheduleLineStatusText: row && row.scheduleLineStatusText,
      fgMaterialCode: row && row.fgMaterialCode,
      fgMaterialName: row && row.fgMaterialName,
      targetQuantity: row && (row.targetQuantity != null ? row.targetQuantity : row.poQuantity),
      targetQuantityUOM: row && (row.targetQuantityUOM || row.batchUOM),
      vendorName: (row && row.vendorName) || (detail && detail.cmoMember),
      sponsorName: row && row.brandOwnerName,
      vendorPlant: (row && row.vendorPlant) || (allocRes && allocRes.vendorPlant),
      storageLocation: (row && row.storageLocation) || (allocRes && allocRes.storageLocation),
      vendorMosymphonyID: row && row.vendorMosymphonyID,
    }),
    [row, detail, allocRes]
  );

  // ── Confirm Consumption (bowls payload replicated from ReviewPopUp) ───────
  const handleConfirm = () => {
    setConfirming(true);
    const bowls = [];
    alloc.forEach((val, index) => {
      if ((val.lotNumberAllocationDetails || []).length > 0) {
        bowls.push({
          ID: index + 1,
          bowl: { value: String.fromCharCode(65 + index) },
          rows: val.lotNumberAllocationDetails
            .filter((lot) => lot.lotNumber && lot.plannedAllocatedQuantity)
            .map((lot, i) => ({
              consumed_material_number: { value: val.componentAllocationDetails && val.componentAllocationDetails.componentCode },
              actual_material_quantity_consumed: { value: lot.plannedAllocatedQuantity },
              consumption_batch_number: { value: lot.lotNumber },
              consumed_material_uom: { value: val.componentAllocationDetails && val.componentAllocationDetails.uom },
              consumption_batch_number_sponsor: { value: lot.lotNumber },
              row_id: i + 1,
            })),
        });
      }
    });
    const confirmSource = axios.CancelToken.source();
    confirmCancelRef.current = confirmSource;
    axios
      .post(
        roleUrl("formConsumptionFromReview"),
        { bowls, poNumber: row.scheduleAgreementNumber, batchNumber: row.vendorBatchNumber },
        { cancelToken: confirmSource.token }
      )
      .then((res) => {
        if (!mountedRef.current) return;
        if (res.data && res.data.status) {
          // Slide 10: the button flips to its confirmed state straight away and the
          // outcome is a toast, not a popup. Confirming consumption advances the
          // batch (55 -> 37), so poll for the status change behind the toast.
          setConfirmed(true);
          feedback.notifySuccess({
            message: "Consumption Confirmed Successfully",
            statusChanging: true,
            batchId: row.schrockenDeliveryScheduleBatchID,
            prevStatus: row.SASLBatchStatusID,
            refetch: onRefresh,
          });
        } else {
          // Not confirmed — leave the button unconfirmed so it can be retried.
          feedback.notifyError();
        }
        setConfirming(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        feedback.notifyError();
        setConfirming(false);
      });
  };

  // Change Allocation opens on the first material; the popup's own material
  // selector switches which one is being edited.
  const openChangeAllocation = () => {
    if (!selectedComponent) setSelectedComponent(alloc[0] || null);
    setChangeOpen(true);
  };

  const closeChangeAllocation = () => {
    setChangeOpen(false);
    load();
  };

  return (
    <SectionShell
      title="Review Consumption"
      headRight={
        <SyncActions
          row={row}
          detail={detail}
          featuresAccess={featuresAccess}
          showSyncInventory
          resetsBatchInventory={true}
          resetsBatchPO={true}
          onRefresh={onRefresh}
          onSyncSuccess={load}
        />
      }
      viewOnly={viewOnly}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" p={2}><CircularProgress size={22} /></Box>
      ) : (
        <React.Fragment>
          <TableContainer component={Paper} style={{ boxShadow: "none", border: "1px solid #E0E4EA" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableCellHead}>BMS Material Code / Description</TableCell>
                  <TableCell className={classes.tableCellHead} align="right">Material BoM Qty.</TableCell>
                  <TableCell className={classes.tableCellHead}>UOM</TableCell>
                  <TableCell className={classes.tableCellHead}>Storage Location ID</TableCell>
                  <TableCell className={classes.tableCellHead}>BMS Material Batch Number</TableCell>
                  <TableCell className={classes.tableCellHead} style={{ whiteSpace: "nowrap" }}>Date of Manufacturing</TableCell>
                  <TableCell className={classes.tableCellHead} style={{ whiteSpace: "nowrap" }}>Expiry Date</TableCell>
                  <TableCell className={classes.tableCellHead} align="right">Planned Allocated Qty</TableCell>
                  <TableCell className={classes.tableCellHead} align="center">Deplete Instruction</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alloc.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ color: "#6B7280" }}>
                      No BoM materials found for this PO line.
                    </TableCell>
                  </TableRow>
                )}
                {alloc.map((comp, ci) => {
                  const d = comp.componentAllocationDetails || {};
                  const lots = allocatedLotsOf(comp);
                  const key = `${d.componentCode || "component"}-${ci}`;
                  if (lots.length === 0) {
                    // No lot data at all for this material (dashboard "Case 3"): the
                    // material-level columns still show, everything else is blank
                    // rather than a collapsed error message.
                    return (
                      <TableRow key={key}>
                        <TableCell>{d.componentCode} / {d.componentName}</TableCell>
                        <TableCell align="right">{formatBomQty(d)}</TableCell>
                        <TableCell>{d.uom}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}></TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}></TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>
                    );
                  }
                  return (
                    <React.Fragment key={key}>
                      {lots.map((lot, li) => (
                        <TableRow key={`${key}-${lot.lotNumber}-${li}`}>
                          {/* The three material-level columns span the material's
                              batch rows (mockup slide 8) rather than repeating. */}
                          {li === 0 && (
                            <TableCell rowSpan={lots.length} style={{ verticalAlign: lots.length > 1 && "top" }}>
                              {d.componentCode} / {d.componentName}
                            </TableCell>
                          )}
                          {li === 0 && (
                            <TableCell rowSpan={lots.length} align="right" style={{ verticalAlign: lots.length > 1 && "top" }}>
                              {formatBomQty(d)}
                            </TableCell>
                          )}
                          {li === 0 && (
                            <TableCell rowSpan={lots.length} style={{ verticalAlign: lots.length > 1 && "top" }}>
                              {d.uom}
                            </TableCell>
                          )}
                          <TableCell style={isSpecialStockWithVendor(lot.storageLocation) ? undefined : { color: "#C0392B" }}>
                            {formatStorageLocation(lot.storageLocation)}
                          </TableCell>
                          <TableCell>{lot.lotNumber}</TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>{blankDate(manufacturingDateOf(comp, lot))}</TableCell>
                          <TableCell
                            style={{
                              whiteSpace: "nowrap",
                              ...(isExpired(lot.expiryDate) ? { color: "#C0392B" } : null),
                            }}
                          >
                            {blankDate(lot.expiryDate)}
                          </TableCell>
                          <TableCell align="right">{formatQty3(lot.plannedAllocatedQuantity)}</TableCell>
                          {/* BCM-363: read-only indicator — greyed and checked where a
                              deplete instruction is held, BLANK (no checkbox at all)
                              where none is. It cannot be set or cleared from here. */}
                          <TableCell align="center">
                            {lot.isDepletedBatch === true && (
                              <Checkbox checked disabled size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={2}>
            {errors.map((e) => (
              <div key={e.id} className={classes.errorLine}>
                <WarningRoundedIcon style={{ fontSize: 18 }} />
                <span>
                  {e.before}
                  <strong>{e.batch}</strong>
                  {e.after}
                </span>
              </div>
            ))}
          </Box>

          <Box mt={2} className={classes.btnRow}>
            <Button className={classes.outlinedBtn} onClick={openChangeAllocation} disabled={viewOnly || alloc.length === 0 || confirming || confirmed}>
              Change Allocation
            </Button>
            <Button className={classes.outlinedBtn} onClick={() => setUploadAllocationOpen(true)} disabled={viewOnly || confirming || confirmed}>
              Upload Allocation
            </Button>
            {confirmed ? (
              <Button className={classes.confirmedBtn} disabled disableElevation endIcon={<CheckIcon />}>
                Consumption Confirmed
              </Button>
            ) : (
              <Button className={classes.orangeBtn} onClick={handleConfirm} disabled={confirmDisabled}>
                {confirming ? "Confirming..." : "Confirm Consumption"}
              </Button>
            )}
          </Box>
        </React.Fragment>
      )}

      {/* Change Allocation — reuse the BATCH DASHBOARD dialog (the same popup
          Batch Execution's ReviewPopUp opens). It owns the material radio selector
          and the CMO | Plant ID | Material BOM Qty. | UOM ribbon internally and
          carries BCM-363, so it renders the mockup as-is. `isFromGoodsReceipt` is
          the ONLY GR-specific input: it restricts the allocation table to
          Special-Stocks-with-Vendor rows. Batch Execution passes nothing, so the
          shared dialog is byte-identical for it. No backdrop close: the dialog owns
          Cancel. */}
      <Dialog open={changeOpen} fullWidth maxWidth="md">
        {changeOpen && (
          <EditPlannedAllocatedQuantityDialog
            lotData={selectedComponent}
            lotAllocationData={alloc}
            lotAllocationRes={allocRes}
            poData={poDataForDialog}
            schrockenSLID={row.schrockenSLID}
            vendorMosymphonyID={row.vendorMosymphonyID}
            isFromGoodsReceipt
            onClose={closeChangeAllocation}
            handleClose={(type) =>
              type === "success"
                ? feedback.notifySuccess({ message: "Component Allocation updated successfully" })
                : feedback.notifyError()
            }
          />
        )}
      </Dialog>

      {/* Upload Allocation — reuse the PO dashboard's "Allocate POs" bulk-upload
          modal, restricted here to THIS batch's PO only (singlePOAllocation +
          poNumber) — the PO dashboard's own usage passes neither, so its
          "every PO in the file" behaviour is unchanged. On close the
          allocation is re-read so an uploaded allocation is reflected in the table
          and re-validated. */}
      <AllocatePOsModal
        open={uploadAllocationOpen}
        singlePOAllocation
        poNumber={row.scheduleAgreementNumber}
        onClose={() => {
          setUploadAllocationOpen(false);
          load();
        }}
      />
    </SectionShell>
  );
};

export default PA3ReviewConsumption;
